/**
 * Wrapper around fetch that automatically handles token refresh
 * This is a bridge solution for legacy fetch() calls
 * Recommendation: Migrate to apiClient.ts (axios) for better error handling
 */

export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Get access token (stored as raw string, not stringified)
  const accessToken = localStorage.getItem('accessToken');
  
  // Add authorization header
  const headers = {
    ...options.headers,
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };

  // Make the request
  let response = await fetch(url, { ...options, headers });

  // If token expired (403), try to refresh and retry
  if (response.status === 403) {
    // Clone before reading body so handleResponse can still consume it if not expired
    const cloned = response.clone();
    try {
      const data = await cloned.json();
      if (data.expired) {
        try {
          // Get refresh token
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) throw new Error('No refresh token available');

          // Call refresh token API
          const refreshResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/teachers/refresh-token`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': '69420',
              },
              body: JSON.stringify({ refreshToken }),
            }
          );

          if (!refreshResponse.ok) throw new Error('Refresh token failed');

          const refreshData = await refreshResponse.json();
          const newAccessToken = refreshData.accessToken;

          // Save new access token
          localStorage.setItem('accessToken', newAccessToken);

          // Retry original request with new token
          const retryHeaders = {
            ...options.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          response = await fetch(url, { ...options, headers: retryHeaders });
        } catch (error) {
          console.error('Token refresh failed:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
          throw error;
        }
      }
      // If not expired, fall through with original response (body already consumed by clone)
      // Return original response so caller gets 403 status
    } catch {
      // If can't parse body, just return the response as-is
    }
  }

  return response;
}
